import { Check, Image as ImageIcon, Info, Loader2, Sparkles } from "lucide-react";
import { type KeyboardEvent, useState } from "react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { SocialAdAlternative, SocialAdPlan, SocialAdRoute, SocialFormat } from "@/lib/api";
import { cn } from "@/lib/utils";

import { AdCopyPreview } from "./social-ad-copy";

/**
 * What the confirm button hands over: an idea, a photograph, and the sizes.
 *
 * Carried as one object rather than three positional arguments because two of
 * the three are optional and a bare `(keys, undefined, "story")` at the call
 * site says nothing about what was chosen.
 */
export interface FormatConfirmation {
  formatKeys: string[];
  /** Set only when an alternative photograph was picked for the chosen route. */
  imageAssetId?: string;
  /**
   * The creative route to render. Absent only on turns remembered from before
   * routes existed, which carry no route to name.
   */
  routeKey?: string;
}

interface SocialAdPlanReviewProps {
  plan: SocialAdPlan;
  onConfirm: (confirmation: FormatConfirmation) => void;
  isBuilding: boolean;
}

/** One selectable photograph — a route's own and the alternatives alike. */
interface Candidate {
  assetId: string;
  previewUrl: string;
  /** The curator's note on this photograph: why it fits the request. */
  reason: string;
  /** The format keys this photograph can actually be laid out in. */
  formatKeys: string[];
}

/**
 * The formats that come pre-ticked.
 *
 * The three sizes a market almost always wants together: the square and the
 * portrait are the two feed shapes, and the vertical covers Stories, Reels and
 * TikTok. Landscape is a LinkedIn-and-link-posts decision rather than a default,
 * so it is offered unticked — starting from zero would make everyone tick the
 * same three by hand every time.
 */
const DEFAULT_FORMAT_KEYS = ["1x1", "4x5", "9x16"];

/**
 * What the composer intends to make, offered before it renders anything.
 *
 * Same shape of moment as the deck plan: the ideas are drafted and shown, and
 * the expensive half — rendering the ad at every size — waits for an answer.
 * Three choices live here, in the order a market actually makes them: which
 * idea, which photograph for it, and which formats.
 *
 * The idea comes first because that is the choice markets said was missing. An
 * earlier version offered one angle sized for every platform, with alternatives
 * that only swapped the photograph behind the same words — so a market could
 * change the picture but never the pitch. Now each route is a different angle
 * carrying its own copy *and* its own photograph, side by side, and the
 * alternatives sit inside the selected one as "another photo for this idea".
 *
 * The formats are asked in platforms rather than ratios, because "Stories,
 * Reels, TikTok" is what someone is actually buying when they tick 9:16.
 */
export function SocialAdPlanReview({ plan, onConfirm, isBuilding }: SocialAdPlanReviewProps) {
  // Turns remembered from before these fields existed carry neither — an older
  // plan still renders, from its flat fields, exactly as it always did.
  const routes = plan.routes ?? [];
  const alternatives = (plan.alternatives ?? []).map(toCandidate);

  const [routeKey, setRouteKey] = useState(() => initialRouteKey(plan));
  // Null means "the photograph this idea came with". Reset on every route
  // change, because each route brought its own and a swap belongs to one idea.
  const [imageOverride, setImageOverride] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>(() => defaultSelection(plan));
  // Named so a swap can say what it cost, rather than quietly shrinking the
  // tick list — the backend rejects a format the photo cannot do with a 400,
  // and being told afterwards is the worst version of that.
  const [dropped, setDropped] = useState<string[]>([]);

  const activeRoute = routes.find((route) => route.key === routeKey) ?? routes[0];
  // With no routes at all, the plan's flat fields are the only proposal there
  // is, and they behave exactly as the proposed photograph always has.
  const routePhoto = activeRoute ? routeCandidate(activeRoute) : proposedCandidate(plan);
  const active =
    alternatives.find((candidate) => candidate.assetId === imageOverride) ?? routePhoto;

  // The pool for the selected idea: its own photograph first, then every other
  // approved one. The ranking is meaningful, so it reads best-first.
  const pool = [
    routePhoto,
    ...alternatives.filter((candidate) => candidate.assetId !== routePhoto.assetId),
  ];

  const supported = new Set(active.formatKeys);
  const available = plan.formats.filter((format) => supported.has(format.key));
  const heading = planHeading(routes.length, pool.length, plan.formats.length);

  const toggle = (key: string) => {
    setDropped([]);
    setSelected((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );
  };

  /** Keep only the ticks the given photograph can honour, and say which went. */
  const narrowTo = (candidate: Candidate) => {
    const keys = new Set(candidate.formatKeys);
    setDropped(
      plan.formats
        .filter((format) => selected.includes(format.key) && !keys.has(format.key))
        .map((format) => format.label),
    );
    setSelected((current) => current.filter((key) => keys.has(key)));
  };

  const chooseImage = (candidate: Candidate) => {
    if (candidate.assetId === active.assetId) {
      return;
    }
    setImageOverride(candidate.assetId === routePhoto.assetId ? null : candidate.assetId);
    narrowTo(candidate);
  };

  const chooseRoute = (route: SocialAdRoute) => {
    if (route.key === activeRoute?.key) {
      return;
    }
    setRouteKey(route.key);
    setImageOverride(null);
    narrowTo(routeCandidate(route));
  };

  return (
    <section className="border-border bg-surface-muted/60 space-y-4 rounded-lg border p-4">
      <header className="space-y-1">
        <h3 className="font-heading text-foreground text-sm font-bold">{heading.title}</h3>
        <p className="text-muted text-xs leading-5">{heading.blurb}</p>
      </header>

      {routes.length === 0 ? (
        <FlatPlanBody
          plan={plan}
          active={active}
          proposed={routePhoto}
          pool={pool}
          available={available}
          disabled={isBuilding}
          onChooseImage={chooseImage}
        />
      ) : (
        <div className="space-y-2">
          {routes.length > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="type-label text-muted">The idea</h4>
              <p className="text-muted text-xs">
                <span className="text-foreground font-semibold">{routes.length}</span> to choose
                from
              </p>
            </div>
          )}

          {/* Side by side so the angles can be compared, one per row as soon as
              the column is too narrow to read three sets of copy across. */}
          <div
            role={routes.length > 1 ? "radiogroup" : undefined}
            aria-label={routes.length > 1 ? "Which idea to render" : undefined}
            className={cn("grid gap-3", routes.length > 1 && "lg:grid-cols-3")}
          >
            {routes.map((route) => {
              const isSelected = route.key === activeRoute?.key;
              const own = routeCandidate(route);
              return (
                <RouteCard
                  key={route.key}
                  route={route}
                  // An unselected card always shows the photograph the route
                  // arrived with; a swap only applies to the idea in play.
                  photo={isSelected ? active : own}
                  pool={isSelected ? pool : []}
                  formats={plan.formats}
                  ownAssetId={own.assetId}
                  selectable={routes.length > 1}
                  selected={isSelected}
                  disabled={isBuilding}
                  onSelect={() => chooseRoute(route)}
                  onChooseImage={chooseImage}
                />
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="type-label text-muted">Where it will be posted</h4>
          <p className="text-muted text-xs">
            <span className="text-foreground font-semibold">{selected.length}</span> of{" "}
            {available.length} selected
          </p>
        </div>

        {dropped.length > 0 && (
          <p className="border-border bg-card text-foreground flex items-start gap-2 rounded-md border p-2.5 text-xs leading-5">
            <Info className="text-velux mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              This photograph does not come in {joinLabels(dropped)}, so{" "}
              {dropped.length === 1 ? "it was" : "they were"} unticked.
            </span>
          </p>
        )}

        <div className="grid gap-2 sm:grid-cols-2">
          {plan.formats.map((format) => (
            <FormatOption
              key={format.key}
              format={format}
              selected={selected.includes(format.key)}
              available={supported.has(format.key)}
              disabled={isBuilding}
              onToggle={() => toggle(format.key)}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={() =>
            onConfirm({
              formatKeys: selected,
              // Sent only when the proposed photograph was swapped out; left
              // off, the route's own photograph is what gets rendered.
              imageAssetId: active.assetId === routePhoto.assetId ? undefined : active.assetId,
              // Always named when there is a route to name, including when it
              // is the one the composer proposed — saying so costs nothing and
              // is one fewer case than deciding when to stay silent.
              routeKey: activeRoute?.key,
            })
          }
          disabled={isBuilding || selected.length === 0}
        >
          {isBuilding ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              Rendering the ads…
            </>
          ) : (
            <>
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Render {selected.length} format{selected.length === 1 ? "" : "s"}
            </>
          )}
        </Button>
        {!isBuilding && (
          <p className="text-muted text-xs">
            Or tell me what to change below — different copy, another language, another photo.
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * One creative route: an angle, its photograph, and the words that go with it.
 *
 * The whole card is the hit target when there is a choice to make, because the
 * photograph and the copy are what someone is actually weighing up — asking
 * them to find a radio button underneath would be a second decision. With only
 * one route it is a card and nothing more: no tick, no ring, no hover state
 * that implies a comparison that does not exist.
 */
function RouteCard({
  route,
  photo,
  pool,
  formats,
  ownAssetId,
  selectable,
  selected,
  disabled,
  onSelect,
  onChooseImage,
}: {
  route: SocialAdRoute;
  /** The photograph on show: the route's own, or the one swapped in for it. */
  photo: Candidate;
  /** Every photograph on offer for this route — empty unless it is selected. */
  pool: Candidate[];
  formats: SocialFormat[];
  /** The photograph the route arrived with, so a swap can be labelled as one. */
  ownAssetId: string;
  selectable: boolean;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  onChooseImage: (candidate: Candidate) => void;
}) {
  const interactive = selectable && !disabled;
  const swapped = selected && photo.assetId !== ownAssetId;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // A thumbnail inside the card raises its own keydown, and swallowing it
    // here would stop that button from ever firing.
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      role={selectable ? "radio" : undefined}
      aria-checked={selectable ? selected : undefined}
      aria-label={selectable ? route.label : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onSelect : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      className={cn(
        "border-border bg-card flex flex-col gap-2.5 rounded-lg border p-3 text-left transition",
        interactive && "cursor-pointer",
        selectable && selected && "border-velux ring-velux/20 ring-2",
        selectable && !selected && "hover:border-velux/40 opacity-90 hover:opacity-100",
      )}
    >
      <CandidateImage src={photo.previewUrl} className="h-28 w-full rounded-md" />

      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h5 className="font-heading text-foreground text-sm font-bold">{route.label}</h5>
          {selectable && selected && (
            <span className="bg-velux text-velux-foreground flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold">
              <Check className="h-2.5 w-2.5" />
              Selected
            </span>
          )}
          {swapped && (
            <span className="bg-velux-tint text-velux rounded-full px-2 py-0.5 text-[10px] font-semibold">
              Your photo
            </span>
          )}
        </div>
        {/* The copy can run long and the card is narrow, so everything here
            wraps — nothing is truncated, because a half-read headline is not
            something anyone can approve. */}
        <p className="text-muted text-xs leading-5 break-words">{route.rationale}</p>
      </div>

      <AdCopyPreview copy={route.copy} />

      <div className="space-y-1">
        <p className="type-label text-muted">The photo</p>
        <p className="text-foreground text-xs leading-5 break-words">{photo.reason}</p>
      </div>

      {pool.length > 1 && (
        <div className="space-y-1.5">
          <p className="type-label text-muted">Another photo for this idea</p>
          {/* A strip rather than a grid: the ranking is meaningful, and a row
              reads left-to-right as "best first, then these". */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {pool.map((candidate) => (
              <CandidateThumbnail
                key={candidate.assetId}
                candidate={candidate}
                formats={formats}
                originLabel={
                  candidate.assetId === ownAssetId ? "This idea's photograph" : "Alternative"
                }
                selected={candidate.assetId === photo.assetId}
                disabled={disabled}
                onSelect={() => onChooseImage(candidate)}
              />
            ))}
          </div>
          <p className="text-faint text-[11px] leading-4">
            These keep this idea&apos;s words and change only the picture. Hover to read why one was
            shortlisted; click to use it instead.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * A plan from before creative routes existed, rendered from its flat fields.
 *
 * Unchanged on purpose: a remembered turn has to keep looking like the thing
 * that was approved, and there is no route on it to show.
 */
function FlatPlanBody({
  plan,
  active,
  proposed,
  pool,
  available,
  disabled,
  onChooseImage,
}: {
  plan: SocialAdPlan;
  active: Candidate;
  proposed: Candidate;
  pool: Candidate[];
  available: SocialFormat[];
  disabled: boolean;
  onChooseImage: (candidate: Candidate) => void;
}) {
  return (
    <>
      {/* Why this photo: the choice was made for them, so it has to justify
          itself — and it has to be visibly replaceable. */}
      <div className="border-border bg-card flex gap-3 rounded-lg border p-3">
        <CandidateImage
          src={active.previewUrl}
          className="h-28 w-28 shrink-0 rounded-md sm:h-32 sm:w-32"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h4 className="type-label text-muted">The photo</h4>
            {active.assetId !== proposed.assetId && (
              <span className="bg-velux-tint text-velux rounded-full px-2 py-0.5 text-[10px] font-semibold">
                Your choice
              </span>
            )}
          </div>
          <p className="text-foreground mt-1 text-xs leading-5">{active.reason}</p>
          <p className="text-muted mt-1.5 text-[11px] leading-4">
            Fits {available.length} of {plan.formats.length} format
            {plan.formats.length === 1 ? "" : "s"} in this request
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="type-label text-muted">Approved photographs</h4>
          <p className="text-muted text-xs">
            <span className="text-foreground font-semibold">{pool.length}</span> fit this request
          </p>
        </div>

        {pool.length === 1 ? (
          <p className="text-muted text-xs leading-5">
            This is the only approved photograph that covers every size you asked for — narrow
            shapes rule most of the library out. Ask for fewer sizes and more of it comes into
            range.
          </p>
        ) : (
          <>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {pool.map((candidate) => (
                <CandidateThumbnail
                  key={candidate.assetId}
                  candidate={candidate}
                  formats={plan.formats}
                  originLabel={
                    candidate.assetId === proposed.assetId ? "Proposed photograph" : "Alternative"
                  }
                  selected={candidate.assetId === active.assetId}
                  disabled={disabled}
                  onSelect={() => onChooseImage(candidate)}
                />
              ))}
            </div>
            <p className="text-faint text-[11px] leading-4">
              Hover a photograph to read why it was shortlisted; click to use it instead.
            </p>
          </>
        )}
      </div>

      <AdCopyPreview copy={plan.copy} heading="The copy" />
    </>
  );
}

/** The photo itself, or a placeholder when the library gave no preview. */
function CandidateImage({ src, className }: { src: string; className?: string }) {
  if (!src) {
    return (
      <span
        className={cn(
          "bg-surface-muted border-border text-faint flex items-center justify-center border",
          className,
        )}
      >
        <ImageIcon className="h-5 w-5" />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      className={cn("bg-surface-muted border-border border object-cover", className)}
    />
  );
}

function CandidateThumbnail({
  candidate,
  formats,
  originLabel,
  selected,
  disabled,
  onSelect,
}: {
  candidate: Candidate;
  formats: SocialFormat[];
  /** Where this photograph came from, in the tooltip's own words. */
  originLabel: string;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const fits = formats.filter((format) => candidate.formatKeys.includes(format.key));

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          role="radio"
          aria-checked={selected}
          aria-label={candidate.reason}
          disabled={disabled}
          onClick={onSelect}
          className={cn(
            "relative shrink-0 rounded-md border p-0.5 transition",
            selected
              ? "border-velux ring-velux/20 ring-2"
              : "border-border hover:border-velux/40 opacity-90 hover:opacity-100",
            disabled && "cursor-default",
          )}
        >
          <CandidateImage src={candidate.previewUrl} className="h-14 w-14 rounded-sm" />
          {selected && (
            <span className="bg-velux text-velux-foreground absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full">
              <Check className="h-3 w-3" />
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-64">
        <span className="block font-semibold">{originLabel}</span>
        <span className="mt-0.5 block leading-4">{candidate.reason}</span>
        <span className="mt-1 block opacity-80">
          Comes in{" "}
          {fits.length > 0
            ? joinLabels(fits.map((format) => format.label))
            : "no format in this request"}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

function FormatOption({
  format,
  selected,
  available,
  disabled,
  onToggle,
}: {
  format: SocialFormat;
  selected: boolean;
  /** Whether the chosen photograph can be laid out in this shape at all. */
  available: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      disabled={disabled || !available}
      onClick={onToggle}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition",
        selected
          ? "border-velux ring-velux/20 bg-card ring-2"
          : "border-border bg-card hover:border-velux/40",
        !available &&
          "border-border bg-surface-muted hover:border-border cursor-default opacity-55",
        available && disabled && "cursor-default opacity-70",
      )}
    >
      {/* The shape of the thing, drawn to scale — it reads faster than "9:16"
          for anyone who does not think in ratios. */}
      <span className="flex h-12 w-12 shrink-0 items-center justify-center">
        <span
          className={cn(
            "block h-12 max-h-12 w-auto max-w-12 rounded-[3px] border",
            selected ? "border-velux bg-velux-tint" : "border-border bg-surface-muted",
          )}
          // The ratio comes from the format's own pixel size, so it cannot be a
          // Tailwind class — those are scanned out of the source at build time
          // and this one is only known at runtime.
          style={{ aspectRatio: `${format.width} / ${format.height}` }}
        />
      </span>

      <span className="min-w-0 flex-1">
        <span className="text-foreground block text-sm font-semibold">
          {format.label}
          <span className="text-muted ml-1.5 text-xs font-normal tabular-nums">
            {format.width} × {format.height}
          </span>
        </span>
        <span className="text-muted mt-0.5 block text-[11px] leading-4">
          {available ? format.platforms.join(" · ") : "Not available for the chosen photograph"}
        </span>
      </span>

      <span
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border",
          selected ? "border-velux bg-velux text-velux-foreground" : "border-input",
          !available && "border-input/60",
        )}
      >
        {selected && <Check className="h-3 w-3" />}
      </span>
    </button>
  );
}

/**
 * What the card leads with, which depends on how much choice there is.
 *
 * Three routes is an invitation to compare; one is a single proposal and must
 * not be dressed up as a shortlist; none is an older turn, which still talks
 * about photographs because photographs were the only choice it offered.
 */
function planHeading(
  routeCount: number,
  poolSize: number,
  formatCount: number,
): { title: string; blurb: string } {
  const sizes = `${formatCount} format${formatCount === 1 ? "" : "s"}`;

  if (routeCount > 1) {
    return {
      title: `${routeCount} ways to run this ad, in ${sizes}`,
      blurb:
        "Each one is a different angle, with its own photograph and its own words. Pick the one you want and tick the sizes you need — nothing has been rendered yet, and only the idea you choose will be.",
    };
  }
  if (routeCount === 1) {
    return {
      title: `One way to run this ad, in ${sizes}`,
      blurb:
        "Nothing has been rendered yet — tick the sizes you need and I'll lay this copy over the photograph in each one.",
    };
  }
  return {
    title: `${poolSize === 1 ? "One approved photograph fits" : `${poolSize} approved photographs fit`} this request, in ${sizes}`,
    blurb:
      "Nothing has been rendered yet — pick the photograph you want and tick the sizes you need, and I'll lay the copy over it in each one.",
  };
}

/** The route that starts out selected: the composer's pick, or the first. */
function initialRouteKey(plan: SocialAdPlan): string {
  const routes = plan.routes ?? [];
  const chosen = plan.chosen_route;
  if (chosen && routes.some((route) => route.key === chosen)) {
    return chosen;
  }
  return routes[0]?.key ?? "";
}

/** A route's own photograph, in the same shape as an alternative. */
function routeCandidate(route: SocialAdRoute): Candidate {
  return {
    assetId: route.image_asset_id,
    previewUrl: route.image_preview_url,
    reason: route.image_reason,
    // The route states which shapes its photograph was approved for, so the
    // UI never has to guess — guessing is what would let someone confirm a
    // size the photograph cannot be laid out in. An empty list means none.
    formatKeys: route.formats,
  };
}

/** The photograph the composer picked, on a plan that names no routes. */
function proposedCandidate(plan: SocialAdPlan): Candidate {
  return {
    assetId: plan.image_asset_id,
    previewUrl: plan.image_preview_url,
    reason: plan.image_reason,
    formatKeys: plan.formats.map((format) => format.key),
  };
}

function toCandidate(alternative: SocialAdAlternative): Candidate {
  return {
    assetId: alternative.asset_id,
    previewUrl: alternative.preview_url,
    reason: alternative.reason,
    formatKeys: alternative.formats,
  };
}

/** "1:1", "1:1 and 4:5", "1:1, 4:5 and 9:16" — a sentence, not a list. */
function joinLabels(labels: string[]): string {
  const last = labels.at(-1);
  if (labels.length <= 1 || last === undefined) {
    return labels.join("");
  }
  return `${labels.slice(0, -1).join(", ")} and ${last}`;
}

/**
 * Pre-tick the usual three, or everything on offer if none of them are.
 *
 * Narrowed to what the starting route's photograph can actually be laid out
 * in, so the card never opens with a size ticked that confirming would 400 on.
 * A plan with no routes was built around its single photograph, so everything
 * it offers is on the table.
 */
function defaultSelection(plan: SocialAdPlan): string[] {
  const start = (plan.routes ?? []).find((route) => route.key === initialRouteKey(plan));
  const supported = start ? new Set(start.formats) : null;
  const offered = plan.formats.filter((format) => !supported || supported.has(format.key));
  const usual = offered.filter((format) => DEFAULT_FORMAT_KEYS.includes(format.key));
  return (usual.length > 0 ? usual : offered).map((format) => format.key);
}
